import { supabase } from '../lib/supabase';
import { isDemoMode, getDemoOrgId, getAuthOrgId } from '../utils/demoHelper';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// Check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

export const contractorService = {
  // Get all contractors
  async getAll() {
    try {
      let query = supabase?.from('contractors')?.select('*')?.order('created_at', { ascending: false });

      // Demo mode: use stored demo org_id directly
      if (isDemoMode()) {
        const demoOrgId = getDemoOrgId();
        if (demoOrgId) {
          query = query?.eq('organization_id', demoOrgId);
        }
      } else {
        // Use cached org_id from AuthContext (never stale after token refresh)
        const orgId = getAuthOrgId();
        if (orgId) {
          query = query?.eq('organization_id', orgId);
        }
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching contractors:', error);
      throw error;
    }
  },

  // Get projects assigned to a contractor
  async getAssignedProjects(contractorId) {
    try {
      const { data, error } = await supabase
        ?.from('project_assignments')
        ?.select('*, projects(*, customers(name))')
        ?.eq('contractor_id', contractorId)
        ?.order('assigned_date', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching assigned projects:', error);
      throw error;
    }
  },

  // Get contractor by ID
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('contractors')?.select('*')?.eq('id', id)?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching contractor:', error);
      throw error;
    }
  },

  // Create new contractor
  async create(contractorData) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('User not authenticated');
      const user = session?.user;

      // Use cached org_id from AuthContext
      const organizationId = getAuthOrgId();
      if (!organizationId) throw new Error('Unable to determine user organization');

      const snakeData = toSnakeCase({
        ...contractorData,
        createdBy: user?.id,
        organizationId
      });

      const { data, error } = await supabase
        ?.from('contractors')
        ?.insert(snakeData)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          throw new Error(error.message);
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error creating contractor:', error);
      throw error;
    }
  },

  // Update contractor
  async update(id, contractorData) {
    try {
      const snakeData = toSnakeCase(contractorData);

      const { data, error } = await supabase?.from('contractors')?.update(snakeData)?.eq('id', id)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          throw new Error(error.message);
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating contractor:', error);
      throw error;
    }
  },

  // Delete contractor
  async delete(id) {
    try {
      const { error } = await supabase?.from('contractors')?.delete()?.eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          throw new Error(error.message);
        }
      }

      return true;
    } catch (error) {
      console.error('Error deleting contractor:', error);
      throw error;
    }
  }
};